import { supabase } from './supabaseClient'

// SPRINT 8 — Appels de l'inspection à la demande.
// Toutes les décisions (zone ouverte, Pass, validation d'un inspecteur,
// versement) sont prises CÔTÉ SERVEUR : ce fichier ne fait que transporter.

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

async function jeton() {
  try {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.access_token || anonKey
  } catch (_) {
    return anonKey
  }
}

async function appel(nom, { methode = 'POST', corps = null, requete = '' } = {}) {
  const token = await jeton()
  let res
  try {
    res = await fetch(`${supabaseUrl}/functions/v1/${nom}${requete}`, {
      method: methode,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: anonKey,
      },
      ...(corps ? { body: JSON.stringify(corps) } : {}),
    })
  } catch (_) {
    const err = new Error('Connexion impossible. Vérifiez votre réseau.')
    err.code = 'network'
    throw err
  }
  const data = await res.json().catch(() => null)
  if (!res.ok) {
    const err = new Error(data?.message || `Erreur ${res.status}.`)
    err.code = data?.error || String(res.status)
    err.status = res.status
    throw err
  }
  return data
}

/* ---------- Côté acheteur ---------- */

/** Configuration publique de l'inspection ; avec un code postal, la
 *  disponibilité réelle du secteur. */
/** Configuration publique de la visio guidée — { ouvert, prix_cents,
 *  part_inspecteur_cents, retenue_cents, attente_absence_minutes,
 *  rapport_heures, mediateur, cgv_version }. */
export const getVisioConfig = () =>
  appel('inspection-checkout', { methode: 'GET', requete: '?type=visio' })

/** Commande une visio. Le créneau et la demande d'exécution immédiate sont
 *  exigés côté serveur. */
export const commanderVisio = (payload) =>
  appel('inspection-checkout', { corps: { ...payload, type: 'visio' } })

/** Annule une visio. Même barème serveur que l'inspection, type « visio ». */
export const annulerVisio = (inspectionId) =>
  appel('inspection-annuler', { corps: { inspection_id: inspectionId } })

/** Lien de l'appel. L'accès est décidé SERVEUR : hors de la fenêtre
 *  (15 min avant, 90 min après), cette fonction refuse — le lien seul n'est
 *  pas un droit d'entrée. */
export const accesVisio = (inspectionId) =>
  appel('visio-acces', { corps: { inspection_id: inspectionId } })

/** L'acheteur autorise les captures d'écran pendant l'appel. */
export async function autoriserCaptures(inspectionId) {
  const { data, error } = await supabase.rpc('visio_consentement_captures', {
    p_inspection_id: inspectionId,
  })
  if (error) throw error
  return data
}

export const getInspectionConfig = (codePostal = null) =>
  appel('inspection-checkout', { methode: 'GET', requete: codePostal ? `?cp=${encodeURIComponent(codePostal)}` : '' })

/** Disponibilité d'une zone (RPC publique, sans révéler les inspecteurs). */
export async function inspectionZone(codePostal) {
  const { data, error } = await supabase.rpc('inspection_zone', { p_code_postal: codePostal })
  if (error) throw error
  return data
}

/** Commande : rend { url } vers la page de paiement Stripe. */
export const commanderInspection = (payload) =>
  appel('inspection-checkout', { corps: payload })

/** Les inspections de l'acheteur connecté. */
export async function mesInspections() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return []
  const { data, error } = await supabase.rpc('mes_inspections')
  if (error) throw error
  return Array.isArray(data) ? data : []
}

/** Photos d'un rapport, déjà signées (5 min) — [{ url, organe, legende }].
 *
 *  La signature se fait CÔTÉ SERVEUR : une policy storage ne peut pas décider
 *  ici, puisqu'elle devrait lire des tables volontairement fermées (RLS sans
 *  policy). L'audit du 2 novembre a montré que l'ancienne policy était
 *  toujours fausse : l'acheteur ne voyait jamais les photos qu'il avait
 *  payées, et l'écran n'affichait même pas d'erreur. */
export async function photosRapport(inspectionId) {
  const { photos } = await appel('inspection-photo', { corps: { inspection_id: inspectionId } })
  return Array.isArray(photos) ? photos : []
}

/* ---------- Côté inspecteur ---------- */

export async function monProfilInspecteur() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return null
  const { data, error } = await supabase.rpc('mon_profil_inspecteur')
  if (error) throw error
  return data
}

export async function candidaterInspecteur(p) {
  const { error } = await supabase.rpc('inspecteur_candidater', {
    p_nom: p.nom,
    p_telephone: p.telephone,
    p_siret: p.siret,
    p_raison_sociale: p.raisonSociale || null,
    p_rc_assureur: p.rcAssureur || null,
    p_rc_numero: p.rcNumero || null,
    p_rc_echeance: p.rcEcheance || null,
    p_departements: p.departements,
  })
  if (error) throw error
}

export async function signerCharte(version) {
  const { error } = await supabase.rpc('inspecteur_signer_charte', { p_version: version })
  if (error) throw error
}

export const deposerAttestation = (document) => appel('inspecteur-dossier', { corps: { document } })

/** Lien d'onboarding Stripe Connect (compte de l'appelant uniquement). */
export const lienConnect = () => appel('inspecteur-connect', { corps: {} })
export const etatConnect = () => appel('inspecteur-connect', { corps: { action: 'etat' } })

export async function inspecteurMissions() {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return []
  const { data, error } = await supabase.rpc('inspecteur_missions')
  if (error) throw error
  return Array.isArray(data) ? data : []
}

export const accepterMission = (inspectionId, creneau = null) =>
  appel('inspecteur-mission', { corps: { action: 'accepter', inspection_id: inspectionId, creneau } })

export const refuserMission = (inspectionId, motif = null) =>
  appel('inspecteur-mission', { corps: { action: 'refuser', inspection_id: inspectionId, motif } })

export const consentirPhotos = (inspectionId) =>
  appel('inspecteur-mission', { corps: { action: 'consentement_photos', inspection_id: inspectionId } })

export const rendreRapport = (payload) => appel('inspection-rapport', { corps: payload })

/** Vendeur absent / véhicule indisponible, constaté SUR PLACE. Une photo
 *  horodatée est exigée : la retenue est prélevée sur l'acheteur, elle ne peut
 *  pas reposer sur la seule parole de celui qui la touche. */
export const constatAbsence = (inspectionId, photoDataUrl) =>
  appel('inspecteur-mission', {
    corps: { action: 'absence_vendeur', inspection_id: inspectionId, photo: photoDataUrl },
  })

/* ---------- Côté acheteur : annulation ---------- */

/** Annule une inspection. Le sort de l'argent est décidé SERVEUR (barème des
 *  CGV) : le client ne propose aucun montant. */
export const annulerInspection = (inspectionId) =>
  appel('inspection-annuler', { corps: { inspection_id: inspectionId } })
